import { ConfirmModal, Toasts, useModalContext } from '@fourthwall/components'
import type { CreatorIntegrationsApi } from '@fourthwall/services/dist/CreatorIntegrationsApiClient/models'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'

import { useDeletePledgeShopCharityMutation } from '../queries/useDeletePledgeShopCharityMutation'
import { usePledgeShopCharityQuery } from '../queries/usePledgeShopCharityQuery'
import { useSetPledgeShopCharityMutation } from '../queries/useSetPledgeShopCharityMutation'
import { useSetPledgeShopCharityPercentageMutation } from '../queries/useSetPledgeShopCharityPercentageMutation'

export const usePledge = () => {
  const { currentShop } = useCurrentShop()
  const shopId = currentShop?.id

  const { pledgeShopCharityQuery, invalidatePledgeShopCharityQuery } = usePledgeShopCharityQuery(
    [{ shopId: shopId! }],
    {
      enabled: !!shopId,
    },
  )
  const { setPledgeShopCharityMutation } = useSetPledgeShopCharityMutation()
  const { setPledgeShopCharityPercentageMutation } = useSetPledgeShopCharityPercentageMutation()
  const { deletePledgeShopCharityMutation } = useDeletePledgeShopCharityMutation()
  const { open } = useModalContext()

  const setCharity = async (
    requestBody: CreatorIntegrationsApi.SetPledgeShopCharity.RequestBody,
  ) => {
    if (!shopId) {
      throw new Error('Shop ID is required')
    }

    try {
      await setPledgeShopCharityMutation.mutateAsync([{ shopId }, requestBody])
      await invalidatePledgeShopCharityQuery()
    } catch (error) {
      console.error('Failed to set pledge charity:', error)
      throw error
    }
  }

  const setCharityPercentage = async (
    requestBody: CreatorIntegrationsApi.SetPledgeShopCharityPercentage.RequestBody,
  ) => {
    if (!shopId) {
      throw new Error('Shop ID is required')
    }

    try {
      await setPledgeShopCharityPercentageMutation.mutateAsync([{ shopId }, requestBody])
      await invalidatePledgeShopCharityQuery()
      Toasts.notify(`Set to ${requestBody.percentage}%.`, { type: 'success' })
    } catch (error) {
      console.error('Failed to set pledge charity percentage:', error)
      throw error
    }
  }

  const deleteCharity = () => {
    if (!shopId) {
      throw new Error('Shop ID is required')
    }

    open(ConfirmModal, {
      title: 'Are you sure you want to remove your Pledge charity?',
      confirmAppearance: 'destructive',
      onConfirm: async () => {
        try {
          await deletePledgeShopCharityMutation.mutateAsync([{ shopId }])
          await invalidatePledgeShopCharityQuery()
        } catch (error) {
          console.error('Failed to delete pledge charity:', error)
        }
      },
    })
  }

  return {
    pledge: {
      charity: pledgeShopCharityQuery.data,
      isLoading: pledgeShopCharityQuery.isLoading,
      isError: pledgeShopCharityQuery.isError,
    },
    setCharity,
    setCharityPercentage,
    deleteCharity,
    isLoading:
      pledgeShopCharityQuery.isLoading ||
      setPledgeShopCharityMutation.isLoading ||
      setPledgeShopCharityPercentageMutation.isLoading ||
      deletePledgeShopCharityMutation.isLoading,
  }
}
